// src/web-app/handlers/file-upload.js
// Web app file upload handler - no Tauri, no database
import { generateDocumentId } from '../../common/api.js';
import { parsePdfFootnotes } from './pdf-parsing.js';

async function readFileBytesForOcr(file) {
  try {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(new Uint8Array(e.target.result));
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  } catch (err) {
    throw err;
  }
}

export function setupFileUpload({
  fileInput,
  btnSelectPdf,
  pickedNameEl,
  btnParsePdf,
  statusEl,
  getAccessToken,
  getClientId,
  apiBase,
  footnoteListEl,
  mgr,
  updateGenerateReportButton,
  btnParseInline,
  btnVerifyAll
}) {
  let picked = null; // {name, bytes}
  let documentId = null;

  const getPicked = () => picked;
  const getDocumentId = () => documentId;

  const activateDocumentContext = () => {
    if (!documentId || !mgr) return;
    mgr.clearState?.();
    if (typeof mgr.setDocumentId === 'function') {
      mgr.setDocumentId(documentId);
    } else {
      mgr.currentDocumentId = documentId;
    }
    window.dispatchEvent(new CustomEvent('fc:document-context-updated', {
      detail: { documentId, fileName: picked?.name || null }
    }));
  };

  const setExistingDocument = ({ id, name }) => {
    documentId = id || null;
    picked = name ? { name, bytes: null } : null;

    if (pickedNameEl) {
      pickedNameEl.textContent = name || 'Saved document';
    }
    if (statusEl && name) {
      statusEl.textContent = `Opened saved document — ${name}`;
    }
    if (btnSelectPdf) btnSelectPdf.textContent = 'Pick New File';
    if (btnParsePdf) btnParsePdf.disabled = !picked;
  };

  // Set file programmatically (used when redirected from landing page)
  const setFile = async (file) => {
    if (!file) return;
    
    console.log('[DEBUG] Setting file programmatically:', file.name);
    const bytes = await readFileBytesForOcr(file);

    picked = { name: file.name, bytes };
    documentId = await generateDocumentId(bytes);
    console.log('[DEBUG] Generated document ID from PDF hash:', documentId);
    activateDocumentContext();

    if (pickedNameEl) pickedNameEl.textContent = file.name;
    if (statusEl) statusEl.textContent = `Ready — ${file.name}`;
    if (btnParsePdf) btnParsePdf.disabled = false;
    if (btnSelectPdf) btnSelectPdf.textContent = 'Pick New File';

    try {
      await parsePdfFootnotes({
        fileInput,
        statusEl,
        footnoteListEl,
        apiBase,
        getPicked,
        getDocumentId,
        mgr,
        updateGenerateReportButton,
        btnParseInline,
        btnVerifyAll
      });
    } catch (err) {
      console.error('[DEBUG] Auto-parse failed:', err);
    }
  };

  // Browser: button click triggers file input
  btnSelectPdf?.addEventListener('click', (e) => {
    e.preventDefault();
    fileInput?.click();
  });

  // Browser: when the hidden input changes
  fileInput?.addEventListener('change', async () => {
    console.log('[DEBUG] File input change event fired');
    const f = fileInput.files && fileInput.files[0];
    console.log('[DEBUG] File selected:', f);
    
    if (!f) { 
      console.log('[DEBUG] No file selected');
      if (statusEl) statusEl.textContent = 'Idle'; 
      return; 
    }

    console.log('[DEBUG] File details:', { name: f.name, size: f.size, type: f.type });
    const bytes = await readFileBytesForOcr(f);

    picked = { name: f.name, bytes };
    documentId = await generateDocumentId(bytes);
    console.log('[DEBUG] Generated document ID from PDF hash:', documentId);
    activateDocumentContext();

    if (pickedNameEl) pickedNameEl.textContent = f.name;
    if (statusEl) statusEl.textContent = `Ready — ${f.name}`;
    if (btnParsePdf) btnParsePdf.disabled = false;
    
    // Update button text to "Choose New File"
    if (btnSelectPdf) btnSelectPdf.textContent = 'Pick New File';

    try {
      await parsePdfFootnotes({
        fileInput,
        statusEl,
        footnoteListEl,
        apiBase,
        getPicked,
        getDocumentId,
        mgr,
        updateGenerateReportButton,
        btnParseInline,
        btnVerifyAll
      });
    } catch (err) {
      console.error('[DEBUG] Auto-parse failed:', err);
    }
  });

  return { getPicked, getDocumentId, setFile, setExistingDocument };
}



